/**
 * Player (PRD §14): one instance per slot. Explicit state machine:
 * idle → requesting → ad → content → finished | closed | fallback-display | fallback-adsense | collapsed.
 * Every transition goes through setState(), which logs in debug mode.
 */
import { LOCKED } from './config.js';
import { createAdController, loadIma } from './ad-controller.js';
import { createPlayedTracker, createPlayerCounters, nextPrerollDecision } from './ad-budget.js';
import { createFallback } from './fallback.js';
import { createFloater, isFloatAllowed } from './floater.js';
import { buildPrebidVideoUnit, runPrebidAuction } from './prebid.js';
import { HEADER_HEIGHT, floatSize, isAdSizeAllowed, pickProfile, playerSize, slotHeights } from './size-profile.js';
import { buildSlotDom, formatTime, showSlotState } from './ui.js';
import { buildVastUrl, makeCorrelator } from './vast-url.js';

/**
 * Release the content source so the browser stops downloading (§3.2 step 1, §10 COST-4).
 * @param {HTMLVideoElement} video
 */
export function releaseVideoSource(video) {
  if (!video) return;
  try {
    video.pause();
  } catch (error) {
    // Ignore.
  }
  if (video.hasAttribute('src')) {
    video.removeAttribute('src');
    try {
      video.load();
    } catch (error) {
      // jsdom and some browsers throw on load() without a source.
    }
  }
}

/**
 * Create one player for a server-rendered slot.
 * @param {object} ctx see app.js for the full shape
 * @returns {object}
 */
export function createPlayer(ctx) {
  const { id, slot, env, config, coordinator, budget, telemetry, safeArea, article } = ctx;
  const { win, doc } = env;
  const schedule = config.adSchedule;
  const playerConfig = ctx.playerConfig;

  const self = {
    id,
    state: 'idle',
    visibleRatio: 0,
    visible: false,
    fullyOut: true,
    decided: false,
    closed: false,
    clickToPlay: false,
    muted: LOCKED.startMuted, // C2: every page view starts muted
    clipIndex: 0,
    pendingBreak: null,
    adClicked: false,
    heldAd: null,
    pausedBy: { tab: null, scroll: false, yield: false },
    destroyed: false,
    noNewRequests: false,
    userCancelledAutoAdvance: false,
    retriedClip: false,
    lastRequest: null,
    lastFloatDecision: null,
    milestones: new Set(),
    profile: 'desktop',
    size: null,
    adDuration: 0,
    captionsUserSet: false,
  };
  const counters = createPlayerCounters();
  const tracker = createPlayedTracker();
  const timers = new Set();
  const cleanups = [];
  let refs = null;
  let adController = null;
  let fallback = null;
  let floater = null;
  let floatRetry = null;
  let observer = null;
  let upNextTimer = null;
  let idleTimer = null;
  let visibleWaiters = [];

  // ---- helpers ------------------------------------------------------------

  /**
   * Debug + telemetry log for state transitions.
   * @param {string} type
   * @param {object} data
   */
  const debug = (type, data) => ctx.debug(id, type, data);

  /**
   * setTimeout tracked for teardown.
   * @param {Function} fn
   * @param {number} ms
   * @returns {*}
   */
  function later(fn, ms) {
    const handle = env.setTimeout(() => {
      timers.delete(handle);
      safeCall(fn)();
    }, ms);
    timers.add(handle);
    return handle;
  }

  /** @param {*} handle */
  function cancel(handle) {
    if (handle === null || handle === undefined) return;
    env.clearTimeout(handle);
    timers.delete(handle);
  }

  /**
   * Wrap a callback in try/catch: any unexpected error collapses safely (ERR-10, §14).
   * @param {Function} fn
   * @returns {Function}
   */
  function safeCall(fn) {
    return (...args) => {
      try {
        return fn(...args);
      } catch (error) {
        onUnexpectedError(error);
        return undefined;
      }
    };
  }

  /**
   * Listen and remember for teardown.
   * @param {EventTarget} target
   * @param {string} type
   * @param {Function} handler
   * @param {object|boolean} [options]
   */
  function listen(target, type, handler, options) {
    const wrapped = safeCall(handler);
    target.addEventListener(type, wrapped, options);
    cleanups.push(() => target.removeEventListener(type, wrapped, options));
  }

  /**
   * The one transition function (§14).
   * @param {string} next
   * @param {object} [detail]
   */
  function setState(next, detail = {}) {
    const previous = self.state;
    self.state = next;
    debug('state', { from: previous, to: next, detail });
    // 2.0.18: an ad starting or ending in the float changes the float size (clip 16:9 <-> ad size): re-dock it.
    if (floater && floater.state.floating && (next === 'ad') !== (previous === 'ad')) {
      floater.scheduleRecheck(safeCall(onFloatLost));
    }
  }

  /** @returns {object} current clip */
  const currentClip = () => ctx.playlist[self.clipIndex];
  const isPhone = () => self.profile === 'phone';
  const holdsToken = () => coordinator.token.holder === id;
  const isAdPlaying = () => self.state === 'ad' && adController && adController.isAdPlaying();
  const isContentPlaying = () => self.state === 'content' && refs && !refs.video.paused && !refs.video.ended;

  /**
   * 2.0.9: the float size in use. On phones a filled bottom sticky ad (375x50) plus the 300x225 float is over the 30%
   * coverage limit (C16), so after a 'density' block the float drops to 268x201, the smallest 4:3 frame C17 allows.
   * @returns {[number, number]|null}
   */
  const adFloatSize = () => (self.smallFloat ? [268, 201] : floatSize(self.profile, config.sizeProfiles));

  /**
   * 2.0.18: the float box size now. On phones the poster and clips float at sizeProfiles.phone.clipFloat (300x169,
   * 16:9 like the clips: no bars); only while an ad plays does it take the ad float size (C17: at least 241x201).
   * @returns {[number, number]|null}
   */
  function floatNow() {
    const clip = isPhone() && config.sizeProfiles.phone.clipFloat;
    return clip && self.state !== 'ad' ? clip.slice() : adFloatSize();
  }

  /** @returns {[number, number]|null} the size the ad would play at right now (C17, FL-12) */
  function currentAdSize() {
    if (floater && floater.state.floating) return adFloatSize();
    return self.size;
  }

  /** @returns {boolean} the reader can see the player (in article >= 50%, or floating) */
  function canBeSeen() {
    if (doc.visibilityState === 'hidden') return false;
    return self.visible || !!(floater && floater.state.floating);
  }

  /**
   * Resolve once the slot is >= 50% visible (WF-6, §3.2 step 2).
   * @returns {Promise<void>}
   */
  function whenVisible() {
    if (self.visible && doc.visibilityState !== 'hidden') return Promise.resolve();
    return new Promise((resolve) => visibleWaiters.push(resolve));
  }

  /** @returns {boolean} config.floatOnArrival and the reader is already past the slot (2.0.8, against C9/C10) */
  const pastSlot = () => !!config.floatOnArrival && self.fullyOut && aboveViewport();

  /** @returns {boolean} the slot is entirely above the viewport: the reader has scrolled past it */
  function aboveViewport() {
    return slot.getBoundingClientRect().bottom <= 0;
  }

  /** @returns {boolean} the slot is entirely below the viewport (CLS rule for collapse) */
  function isBelowViewport() {
    return slot.getBoundingClientRect().top >= win.innerHeight;
  }

  /**
   * Telemetry helper.
   * @param {string} name
   * @param {object} [params]
   */
  function track(name, params = {}) {
    telemetry.track(
      name,
      Object.assign(
        {
          player_id: id,
          slot_index: ctx.index + 1,
          size_profile: self.profile,
          is_floating: !!(floater && floater.state.floating),
        },
        params,
      ),
    );
  }

  // ---- layout (§7.2) -----------------------------------------------------

  /**
   * Compute the size profile and apply the in-article player size.
   * The slot's reserved height only changes while it is entirely below the viewport (SZ-7).
   * @param {boolean} initial first layout at build time
   */
  function applyLayout(initial) {
    const width = win.innerWidth;
    const profile = pickProfile(width, config.sizeProfiles);
    // The slot's own width: the column on desktop/tablet; on phones the full-bleed width the site printed, or the column
    // when the theme clips (overflow: hidden), so no part of the player or IMA's ad UI is ever cut off (C6).
    const columnWidth = slot.getBoundingClientRect().width || width;
    const size = playerSize({
      profileName: profile,
      profiles: config.sizeProfiles,
      viewportWidth: width,
      viewportHeight: win.innerHeight,
      columnWidth: Math.min(columnWidth, width),
    });
    self.profile = profile;
    self.size = size;
    if (!refs) return;
    const heights = slotHeights(profile, size);
    if (initial || isBelowViewport()) refs.body.style.height = heights.header + heights.frame + 'px';
    if (size && !(floater && floater.state.floating)) {
      refs.player.style.width = size[0] + 'px';
      refs.stage.style.aspectRatio = size[0] + '/' + size[1]; // 16:9, or 4:3 on narrow phones
      const spare = heights.frame - size[1];
      refs.player.style.marginTop = spare > 0 ? Math.floor(spare / 2) + 'px' : '0';
    }
    // §7.2: resize the ad in place (never re-request). 2.0.5 (A51): on every layout pass while an ad is showing,
    // not only when the profile changes, so it follows float and un-float: before, an ad started in the slot was
    // clipped in the smaller float (C6), and a loop pre-roll started floating stayed float-sized back in the slot.
    if (self.state === 'ad' && adController && !doc.fullscreenElement) {
      const adSize = currentAdSize();
      if (adSize) adController.resize(adSize[0], adSize[1], false);
    }
    // SZ-9: below 360 px, pause playback, stop new ad requests, leave the slot as it is.
    if (profile === 'narrow') {
      self.noNewRequests = true;
      pauseEverything('small_screen');
    } else {
      self.noNewRequests = false;
    }
  }

  // ---- UI states (§7) -----------------------------------------------------

  /** @returns {string} poster of the current clip */
  const posterUrl = () => (currentClip() ? currentClip().poster : '');

  /**
   * Render a slot state.
   * @param {string} kind
   * @param {object} [options]
   */
  function show(kind, options = {}) {
    if (!refs) return;
    showSlotState(refs, kind, options);
    if (kind === 'player' || kind === 'floating') refs.player.hidden = false;
  }

  /**
   * Show the player with a mode (ad/content).
   * @param {'ad'|'content'|'loading'} mode
   */
  function showPlayer(mode) {
    if (!refs) return;
    refs.player.setAttribute('data-mode', mode === 'loading' ? 'content' : mode);
    // Spinner while a clip loads, and (2.0.5, A49) from the ad's start until IMA reports it playing.
    refs.player.toggleAttribute('data-loading', mode === 'loading' || mode === 'ad');
    // A41: during an ad the title bar says "Advertisement", so an ad (in the slot or floating) never reads as content.
    refs.title.textContent = mode === 'ad' ? 'Advertisement' : currentClip() ? currentClip().title : '';
    if (mode !== 'content') refs.poster.style.display = mode === 'ad' ? 'none' : '';
    if (floater && floater.state.floating) show('floating', { posterUrl: posterUrl(), dock: floater.state.dock });
    else show('player');
  }

  /** Watch button state after finish/close/empty (C25, C27: no live ad in the slot). */
  function showWatch(text) {
    show('closed', { posterUrl: posterUrl(), buttonText: text, onButton: (event) => onUserTap(event, text) });
  }

  // ---- the decision at 50% visible (§3, §6) ------------------------------

  /**
   * First time the slot is >= 50% visible with nothing decided: video or display.
   * 2.0.12 (video.preRequest): also once at build, before the slot is visible, so the pre-roll is ready at 50%.
   * @param {boolean} [early] the pre-request at build
   */
  async function decide(early) {
    if (self.decided || self.destroyed) return;
    self.decided = true;
    self.early = !!early;
    const videoBlocked =
      !ctx.playlist.length || // C24: no on-niche clip -> no player
      !ctx.videoAllowed || // IVT-2
      !ctx.descriptionUrl || // C28
      self.noNewRequests ||
      !isAdSizeAllowed(currentAdSize()); // C17
    if (videoBlocked) return toDisplay(!ctx.playlist.length ? 'no_clips' : 'video_blocked');
    const decision = await coordinator.acquire(id, () => self.early || self.visible || pastSlot());
    if (self.destroyed) return;
    if (decision === 'video') return requestBreak(0, 'auto');
    if (decision === 'display') return toDisplay('token_held');
    if (decision === 'retry') {
      self.decided = false; // scrolled away while waiting: decide again from scratch later
      return undefined;
    }
    // Partner present: click-to-play only (§6, §12).
    self.clickToPlay = true;
    showWatch('▶\uFE0E Watch video');
    return undefined;
  }

  // ---- ad breaks (§3, §4) ------------------------------------------------

  /** @returns {Promise<object>} google.ima, loaded once per page (ERR-1: 5 s) */
  const imaSdk = () =>
    loadIma({ win, loadScript: env.loadScript, timeoutMs: LOCKED.imaLoadTimeoutMs, setTimeoutFn: env.setTimeout });

  /** Make sure IMA and the ad controller exist. */
  async function ensureAdController() {
    if (adController) return adController;
    const ima = await imaSdk();
    if (self.destroyed) throw new Error('destroyed');
    adController = createAdController({
      ima,
      video: refs.video,
      adContainer: refs.ad,
      setTimeoutFn: env.setTimeout,
      clearTimeoutFn: env.clearTimeout,
      onAdEvent: safeCall(onAdEvent),
    });
    return adController;
  }

  /**
   * Request the pre-roll before clip `clipIndex`.
   * @param {number} clipIndex
   * @param {'auto'|'click'} startType C19
   */
  async function requestBreak(clipIndex, startType) {
    if (self.destroyed) return;
    const clip = ctx.playlist[clipIndex];
    self.clipIndex = clipIndex;
    const decision = nextPrerollDecision({ budget, counters, clip, clipIndex });
    const sizeOk = isAdSizeAllowed(currentAdSize()); // C17
    if (!decision.request || !sizeOk || self.noNewRequests) {
      const reason = sizeOk ? decision.reason : 'player_too_small';
      debug('preroll_skipped', { reason });
      // 2.0.8: in GA4 too, so loop and next-clip pre-roll skips can be reviewed per reason.
      track('qsvp_preroll_skipped', { reason, clip_index: clipIndex + 1 });
      if (startType === 'click') return playClip(clipIndex, 'tap'); // C25: a tapped clip always plays
      if (self.looping) return playClip(clipIndex, 'auto'); // A40: no ad allowed for this loop: replay without one
      if (clipIndex === 0) {
        coordinator.release(id, 'no_ad');
        return toDisplay(decision.reason); // C4: automatic plays never load content without an ad
      }
      return stopAutoAdvance(clipIndex); // assumption A11
    }
    setState('requesting', { clipIndex, startType });
    const floating = floater && floater.state.floating; // 2.0.14: a poster retry keeps the float
    if (clipIndex === 0 && startType === 'auto' && !self.looping && !floating) show('placeholder', { posterUrl: posterUrl() });
    else showPlayer('loading');
    const pending = { clipIndex, startType, breakNumber: budget.state.adsServed + 1 };
    self.pendingBreak = pending;
    try {
      await ensureAdController();
    } catch (error) {
      // ERR-1: IMA blocked or not ready in 5 s -> tier 2.
      track('qsvp_ad_error', { ad_break: pending.breakNumber, error_code: 'ima_load', stage: 'load' });
      return onNoAd(pending);
    }
    if (self.destroyed || self.pendingBreak !== pending) return undefined;
    const consent = await ctx.consentPromise; // PRV-1: consent known before any request
    const size = currentAdSize();
    const usePrebid = ctx.usePrebid;
    const prebidStart = env.now();
    let baseUrl = null;
    if (usePrebid) {
      const unit = buildPrebidVideoUnit({
        code: config.prebid.adUnitCode,
        playerSize: size,
        startType,
        maxDurationSeconds: Math.floor(decision.maxAdDurationMs / 1000),
        bidders: config.prebid.bidders,
      });
      // DEM-6: consent is resolved above, so no bidder fires before the CMP is ready.
      baseUrl = await runPrebidAuction({
        pbjs: win.pbjs,
        unit,
        timeoutMs: config.prebid.timeoutMs,
        gamParams: { iu: config.video.adUnit, description_url: ctx.descriptionUrl },
        setTimeoutFn: env.setTimeout,
      });
    }
    if (self.destroyed || self.pendingBreak !== pending) return undefined;
    let url;
    try {
      url = buildVastUrl({
        baseUrl,
        adUnit: config.video.adUnit,
        sizes: config.video.sizes,
        muted: self.muted, // C1: real audio state at request time
        startType, // C19
        autoAdvance: autoAdvanceOn(),
        maxAdDurationMs: decision.maxAdDurationMs,
        pageUrl: ctx.descriptionUrl,
        descriptionUrl: ctx.descriptionUrl,
        cmsid: config.metadata.cmsid,
        clipId: clip.id,
        clipSeconds: clip.duration,
        custParams: Object.assign({}, config.video.custParams, ctx.custParams, { player: id }),
        consent,
        adTest: config.adTest,
        correlator: makeCorrelator(env.random),
      });
    } catch (error) {
      return onNoAd(pending); // C28: description_url missing -> display
    }
    self.lastRequest = url; // the debug add-on parses it for the panel
    debug('ad_request', { url });
    track('qsvp_ad_request', {
      ad_break: pending.breakNumber,
      // 2.0.10: 1 or 2 for a no-fill retry. 2.0.15: poster retries report poster_try (1..posterRetries) instead.
      retry: self.posterTry ? undefined : self.retries,
      poster_try: self.posterTry,
      early: canBeSeen() ? undefined : 1, // 2.0.12: the player was not on screen (2.0.15: floating counts as seen)
      vpa: startType,
      max_ad_s: Math.floor(decision.maxAdDurationMs / 1000),
    });
    const vastTimeout = usePrebid
      ? Math.max(1000, LOCKED.vastTimeoutPrebidMs - (env.now() - prebidStart)) // 5 s total with Prebid
      : config.video.vastTimeoutMs; // 4 s
    adController.request({
      url,
      width: size[0],
      height: size[1],
      muted: self.muted,
      autoplay: startType === 'auto',
      continuousPlayback: autoAdvanceOn(),
      timeoutMs: vastTimeout,
      maxAdSeconds: decision.maxAdDurationMs / 1000,
      bitrate: isPhone() ? config.video.phoneBitrateKbps : config.video.bitrateKbps, // 2.0.13: fewer VAST 402s
    });
    return undefined;
  }

  /**
   * IMA events from the ad controller.
   * @param {string} type
   * @param {object} detail
   */
  function onAdEvent(type, detail) {
    const pending = self.pendingBreak;
    if (!pending) return;
    const adBreak = pending.breakNumber;
    // 2.0.5 (A49): the spinner shows while the ad buffers (and from startAd() until it plays); every other
    // ad event means it is playing, ended or failed.
    refs.player.toggleAttribute('data-loading', type === 'buffering'); // refs exists before any ad request
    switch (type) {
      case 'loaded':
        track('qsvp_ad_fill', { ad_break: adBreak });
        // 2.0.7 (A54, revisits A42 part 3): an ad that loads after the reader scrolled fully past starts straight
        // in the float. Partly visible, tab hidden or no float allowed: held until back in view (FL-5); after 30 s
        // unseen it goes to the display fallback.
        if (canBeSeen() || floatLate()) startAd();
        else {
          holdAd();
          preloadHeld();
        }
        break;
      case 'started':
        budget.countAd();
        counters.adsServed += 1;
        self.adDuration = detail.duration || 0;
        coordinator.markActive(id);
        track('qsvp_ad_start', {
          ad_break: adBreak,
          ad_duration_s: Math.round(detail.duration || 0),
          skippable: detail.skippable,
        });
        // 2.0.2 (A42): the reader can scroll away between startAd() and IMA STARTED. Never let an ad play
        // unseen: pause it; onIntersect() resumes it when the slot is back in view.
        if (!canBeSeen()) pauseInPlace('offscreen_start');
        break;
      case 'progress':
        track('qsvp_ad_progress', { ad_break: adBreak, ad_percent: detail.percent });
        break;
      case 'complete':
        track('qsvp_ad_complete', { ad_break: adBreak });
        break;
      case 'skipped':
        track('qsvp_ad_skip', { ad_break: adBreak });
        break;
      case 'click':
        // C12: content pauses on ad click and does not resume until the user presses play.
        self.adClicked = true;
        track('qsvp_ad_click', { ad_break: adBreak });
        break;
      case 'empty':
        track('qsvp_ad_empty', { ad_break: adBreak, error_code: String(detail.code) });
        if (!retryPreroll(pending, detail.code)) onNoAd(pending);
        break;
      case 'error-before-start':
        track('qsvp_ad_error', { ad_break: adBreak, error_code: String(detail.code), stage: 'load' });
        onNoAd(pending); // ERR-4
        break;
      case 'error-after-start': // ERR-5: the ad failed while playing, so play the clip, as after a normal end
        track('qsvp_ad_error', { ad_break: adBreak, error_code: String(detail.code), stage: 'play' });
      // falls through
      case 'break-ended':
        budget.addAdSeconds(Math.min(detail.adSeconds || 0, self.adDuration || 30));
        if (type === 'break-ended') self.adEndedAt = env.now();
        self.pendingBreak = null;
        playClip(pending.clipIndex, pending.startType === 'click' ? 'tap' : 'auto');
        break;
      case 'script_error':
        onUnexpectedError(detail.error);
        break;
      default:
        break;
    }
  }

  /** Start the loaded ad now (C9: visible >= 50% or already floating in view). */
  function startAd() {
    cancel(self.heldAd);
    self.heldAd = null;
    setState('ad');
    const size = currentAdSize();
    showPlayer('ad');
    setAdButton(true);
    adController.start(size[0], size[1], self.muted);
  }

  /**
   * @returns {boolean} the reader scrolled past the slot and the player could float (A54). 2.0.12: above the
   * viewport only; a pre-requested ad for a slot still below the screen waits for it (C10).
   */
  function floatLate() {
    return self.fullyOut && aboveViewport() && tryFloat();
  }

  /**
   * 2.0.13: a held ad starts buffering its media once any part of the slot is on screen (not when it is fetched, so
   * readers who never get there download no ad video). It still starts only at 50% visible (C9).
   */
  function preloadHeld() {
    if (heldReady() && self.visibleRatio > 0 && adController.preload(...currentAdSize())) debug('ad_preload', {});
  }

  /** @returns {boolean} a loaded ad is waiting to start (FL-5) */
  function heldReady() {
    return self.state === 'requesting' && self.heldAd && adController && adController.hasLoadedAd();
  }

  /** Hold a loaded ad until the slot is back in view; drop it after 30 s (§7 static table). */
  function holdAd() {
    self.heldAd = later(() => {
      self.heldAd = null;
      const pending = self.pendingBreak;
      if (pending && self.early) return dropEarly(); // 2.0.12: the reader has not reached the slot yet
      // 2.0.3: a filled ad nobody saw is not a no-fill; report it separately.
      if (pending) onNoAd(pending, 'ad_expired_offscreen');
      return undefined;
    }, 30000);
  }

  /**
   * 2.0.12: a pre-requested ad waited 30 s for a reader who has not reached the slot. Drop it quietly (no display
   * fallback), release the token and decide again from scratch once the slot is 50% visible.
   */
  function dropEarly() {
    self.pendingBreak = null;
    adController.cancel();
    coordinator.release(id, 'early_expired');
    self.decided = self.early = false;
    self.retries = undefined;
    setState('idle', { reason: 'early_expired' });
  }

  /**
   * 2.0.10: ask again for an empty first pre-roll of an automatic start (video.noFillRetries, at most 2), only while
   * the reader can see the slot. Only IMA's no-ad answers (303 no ads after wrappers, 1009 empty VAST) are retried;
   * timeouts and other errors go straight to the display fallback.
   * @param {object} pending
   * @param {number|string} code IMA error code, or 'timeout'
   * @returns {boolean} a new request is on its way
   */
  function retryPreroll(pending, code) {
    const tries = self.retries || 0;
    if (
      (code !== 303 && code !== 1009) ||
      pending.clipIndex ||
      pending.startType !== 'auto' ||
      self.looping ||
      tries >= config.video.noFillRetries
    )
      return false;
    if (self.pendingBreak !== pending || !(self.early || canBeSeen() || pastSlot())) return false;
    self.retries = tries + 1;
    self.pendingBreak = null;
    adController.cancel();
    later(() => requestBreak(0, 'auto'), 0); // a new correlator, so a new auction
    return true;
  }

  /**
   * No ad for this break (empty, timeout, error before start, IMA blocked, held ad expired).
   * @param {object} pending
   * @param {string} [reason] fallback reason reported to the display tier
   */
  function onNoAd(pending, reason = 'no_fill') {
    if (self.pendingBreak !== pending) return;
    self.pendingBreak = null;
    cancel(self.heldAd);
    self.heldAd = null;
    if (adController) adController.cancel();
    if (pending.startType === 'click') return playClip(pending.clipIndex, 'tap'); // C25
    if (self.looping) return playClip(pending.clipIndex, 'auto'); // A40: unfilled loop pre-roll: replay anyway
    if (pending.clipIndex === 0) {
      self.noFill = true; // 2.0.8: lets the all-empty state play the clip (playOnEmpty)
      if (config.noAdFloat) return toPoster(reason); // 2.0.14: no display fallback
      coordinator.release(id, 'first_ad_empty'); // MP-7
      return toDisplay(reason); // WF-5
    }
    return stopAutoAdvance(pending.clipIndex); // SCH-7
  }

  /**
   * 2.0.14 (noAdFloat, product decision: no display fallback). The first pre-roll did not play: show the poster in
   * the player, keep the token so it can float once the reader passes the slot, and ask for the pre-roll again every
   * video.posterRetrySeconds (at most video.posterRetries times) while the player can be seen. A tap plays the clip
   * with a click-to-play pre-roll (C25). The poster floats only; it never removes sticky ads (stickyMakeRoom).
   * @param {string} reason
   */
  function toPoster(reason) {
    setState('poster', { reason });
    showPlayer('poster');
    if (self.fullyOut && aboveViewport() && !floater.state.floating) tryFloat(); // 2.0.15: no repeat qsvp_float
    self.posterTries = (self.posterTries || 0) + 1;
    if (self.posterTries <= config.video.posterRetries) later(posterRetry, config.video.posterRetrySeconds * 1000);
  }

  /** 2.0.14: the next pre-roll attempt from the poster, once the player is on screen (slot >= 50% or floating). */
  function posterRetry() {
    if (self.state !== 'poster' || self.closed || self.destroyed) return;
    if (!canBeSeen()) return void later(posterRetry, 5000);
    self.posterTry = self.posterTries;
    requestBreak(0, 'auto');
  }

  // ---- content (§4, §10) -------------------------------------------------

  /** @returns {boolean} auto-advance on (C19 vconp, UX-10 reduced motion) */
  function autoAdvanceOn() {
    return !!schedule.autoAdvance && !self.userCancelledAutoAdvance && !env.prefersReducedMotion();
  }

  /**
   * Play a clip. Content only loads after an ad started, or on a user tap (WF-1, COST-1, C25).
   * @param {number} index
   * @param {'auto'|'tap'} startType
   */
  function playClip(index, startType) {
    const clip = ctx.playlist[index];
    if (!clip) return finish('done');
    self.looping = false;
    self.retries = self.posterTry = undefined; // later pre-rolls report no retry count
    self.clipIndex = index;
    self.startType = startType;
    counters.clipsStarted += 1;
    tracker.reset();
    self.milestones = new Set();
    setState('content', { clipIndex: index, startType });
    // 2.0.2 (A42): keep floating from the ad into the clip while the slot is still off screen (§7 slot states).
    if (self.visible) unfloat('ad_end'); // unfloat() does nothing when not floating
    coordinator.markActive(id); // §6: a playing clip holds the token as active, also after a tap without an ad
    showPlayer('content');
    const video = refs.video;
    video.muted = self.muted; // preload stays none from the template (PERF-4): nothing changes it
    // COST-1: the clip URL is attached only now. Bunny requests are cookieless (no crossorigin credentials).
    video.src = clip.src;
    setCaptions(clip);
    refs.poster.src = clip.poster;
    refs.poster.style.display = '';
    track('video_start', {
      video_title: clip.title,
      video_url: clip.src,
      video_duration: clip.duration,
      video_provider: 'qs_player',
      clip_id: clip.id,
      clip_index: index + 1,
      start_type: startType,
    });
    if (self.adClicked) {
      // C12: no auto-resume after an ad click; wait for the user's play.
      self.adClicked = false;
      updatePlayButtons();
      return;
    }
    if (!canBeSeen() && startType !== 'tap') {
      self.pausedBy.scroll = true; // resumes when back in view
      return;
    }
    tryPlay();
  }

  /** play() with the ERR-8 fallback: click-to-play, never retry in a loop. */
  function tryPlay() {
    const video = refs.video;
    let result;
    try {
      result = video.play();
    } catch (error) {
      result = Promise.reject(error);
    }
    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        if (self.destroyed || self.state !== 'content') return;
        if (error && error.name === 'AbortError') return;
        self.clickToPlay = true; // next request sends vpa=click
        showWatch('▶\uFE0E Watch video');
      });
    }
  }

  /**
   * Captions: WebVTT, on by default while muted (UX-1).
   * @param {object} clip
   */
  function setCaptions(clip) {
    const video = refs.video;
    for (const track of Array.from(video.querySelectorAll('track'))) track.remove();
    if (!clip.vtt) return;
    const trackEl = doc.createElement('track');
    trackEl.kind = 'captions';
    trackEl.srclang = 'en';
    trackEl.label = 'English';
    trackEl.src = clip.vtt;
    trackEl.default = true;
    video.appendChild(trackEl);
    syncCaptions();
  }

  /** Captions showing while muted unless the user chose otherwise. */
  function syncCaptions() {
    const tracks = refs.video.textTracks;
    if (!tracks || !tracks.length) return;
    const on = self.captionsUserSet ? refs.captions.getAttribute('aria-pressed') === 'true' : self.muted;
    tracks[0].mode = on ? 'showing' : 'hidden';
    refs.captions.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  /** timeupdate: played seconds, milestones, content cap (§4, §10). */
  function onTimeUpdate() {
    const video = refs.video;
    if (self.state !== 'content') return;
    const added = tracker.sample(video.currentTime, !video.paused && !video.seeking);
    if (added) {
      budget.addContentSeconds(added);
      counters.contentSeconds += added;
    }
    const duration = video.duration || (currentClip() && currentClip().duration) || 0;
    if (duration) {
      const percent = (video.currentTime / duration) * 100;
      for (const milestone of [10, 25, 50, 75]) {
        if (percent >= milestone && !self.milestones.has(milestone)) {
          self.milestones.add(milestone);
          track('video_progress', {
            video_percent: milestone,
            video_current_time: Math.round(video.currentTime),
            clip_id: currentClip().id,
            clip_index: self.clipIndex + 1,
          });
        }
      }
      refs.seek.value = String(Math.round((video.currentTime / duration) * 1000));
      refs.time.textContent = formatTime(video.currentTime) + ' / ' + formatTime(duration);
    }
    if (counters.contentSeconds >= schedule.maxContentSecondsPerView && self.startType !== 'tap') {
      video.pause();
      finish('content_cap'); // COST-2
    }
  }

  /** A clip ended: Up next, next pre-roll, or finish (§4 playlist flow). */
  function onClipEnded() {
    if (self.state !== 'content') return;
    const clip = currentClip();
    const duration = refs.video.duration || clip.duration;
    counters.lastClipPlayedFraction = duration ? tracker.seconds() / duration : 0;
    track('video_complete', { clip_id: clip.id, clip_index: self.clipIndex + 1 });
    const next = self.clipIndex + 1;
    if (next >= ctx.playlist.length || next >= schedule.maxClipsPerView) {
      // A39/A40: loop the last clip, with a pre-roll when the ad budget allows; the locked clip and content
      // caps end the view.
      if (!autoAdvanceOn() || counters.clipsStarted >= schedule.maxClipsPerView) return finish('done');
      self.looping = true;
      return requestBreak(self.clipIndex, 'auto');
    }
    if (!autoAdvanceOn()) return stopAutoAdvance(next);
    showUpNext(next);
  }

  /**
   * "Up next" card for 3 s with a cancel option (SCH-6).
   * @param {number} next
   */
  function showUpNext(next) {
    const nextClip = ctx.playlist[next];
    refs.upNextTitle.textContent = 'Up next: ' + nextClip.title;
    refs.player.setAttribute('data-upnext', '');
    track('qsvp_up_next', { action: 'shown', next_clip_id: nextClip.id });
    upNextTimer = later(() => {
      refs.player.removeAttribute('data-upnext');
      upNextTimer = null;
      requestBreak(next, 'auto');
    }, 3000);
  }

  /** Cancel the Up next card. */
  function cancelUpNext() {
    if (!upNextTimer) return;
    cancel(upNextTimer);
    upNextTimer = null;
    refs.player.removeAttribute('data-upnext');
    self.userCancelledAutoAdvance = true;
    const next = self.clipIndex + 1;
    track('qsvp_up_next', { action: 'cancel', next_clip_id: ctx.playlist[next] && ctx.playlist[next].id });
    stopAutoAdvance(next);
  }

  /**
   * Auto-advance stopped (unfilled pre-roll, budget, cancel): release and show "Watch next".
   * @param {number} nextIndex
   */
  function stopAutoAdvance(nextIndex) {
    self.clipIndex = Math.min(nextIndex, ctx.playlist.length - 1);
    endPlayback('finished');
    setState('finished', { next: nextIndex });
    if (nextIndex < ctx.playlist.length) showWatch('▶\uFE0E Watch next');
    else showWatch('▶\uFE0E Watch again');
  }

  /**
   * Playlist finished.
   * @param {string} reason
   */
  function finish(reason) {
    endPlayback('finished');
    setState('finished', { reason });
    showWatch('▶\uFE0E Watch again');
  }

  /**
   * Shared end of playback: source released, float ended, token released (§6, §10).
   * @param {string} floatReason
   */
  function endPlayback(floatReason) {
    if (refs) releaseVideoSource(refs.video);
    unfloat(floatReason);
    cancel(upNextTimer);
    upNextTimer = null;
    if (refs) refs.player.removeAttribute('data-upnext');
    coordinator.release(id, floatReason);
  }

  /** ERR-7: try the next clip once; if that fails, release the source and show the poster. */
  function onContentError() {
    if (self.state !== 'content') return;
    track('qsvp_player_error', { error_code: 'content_error', stage: 'play' });
    const next = self.clipIndex + 1;
    if (!self.retriedClip && next < ctx.playlist.length) {
      self.retriedClip = true;
      return playClip(next, self.startType || 'auto');
    }
    releaseVideoSource(refs.video);
    return finish('content_error');
  }

  // ---- display waterfall (§3, §3.2) -------------------------------------

  /**
   * Tear the video side down (§3.2 step 1) and hand the slot to the display waterfall.
   * @param {string} reason
   */
  function toDisplay(reason) {
    if (fallback || self.destroyed) return;
    teardownVideo();
    setState('fallback-display', { reason });
    fallback = createFallback({
      win,
      doc,
      config,
      playerId: id,
      slot,
      article,
      getProfile: () => self.profile,
      loadScript: env.loadScript,
      getVisibleRatio: () => self.visibleRatio,
      whenVisible,
      getOverlapRects: () =>
        safeArea
          .obstacles([])
          .filter((obstacle) => obstacle.kind !== 'inline-ad')
          .map((obstacle) => obstacle.rect),
      isBelowViewport,
      consentPromise: ctx.consentPromise,
      childDirected: !!config.privacy.childDirected,
      targeting: Object.assign({}, config.video.custParams, ctx.custParams, { player: id }),
      now: env.now,
      setTimeoutFn: env.setTimeout,
      clearTimeoutFn: env.clearTimeout,
      MutationObserverCtor: env.MutationObserver,
      onEvent: (name, params) => {
        debug(name, params);
        if (name.startsWith('qsvp_') && !name.endsWith('_request')) track(name, params);
      },
      onState: (kind, detail) => onFallbackState(kind, detail),
    });
    fallback.toDisplay();
  }

  /**
   * Slot states reported by the fallback.
   * @param {string} kind
   * @param {object} detail
   */
  function onFallbackState(kind, detail) {
    if (kind === 'fallback-pending' || kind === 'fallback-adsense-pending') {
      show('placeholder', {}); // grey panel, no poster next to a coming ad (C27)
    } else if (kind === 'fallback-display') {
      show('fallback');
    } else if (kind === 'fallback-adsense') {
      setState('fallback-adsense');
      show('fallback');
    } else if (kind === 'collapsed') {
      setState('collapsed', detail);
      show('collapsed');
    } else if (kind === 'all-empty') {
      // Assumption A2 (Q2): with no live ad, the poster and "▶ Watch video" are allowed (§7 table);
      // a refresh that came back empty or a failed container check shows the neutral placeholder.
      const neutral = detail.reason === 'refresh_empty' || detail.reason === 'adsense_blocked' || !ctx.playlist.length;
      if (!neutral && self.noFill && schedule.playOnEmpty && self.visible) return playOnEmpty();
      if (neutral) show('placeholder', {});
      else showWatch('▶\uFE0E Watch video');
      self.allTiersEmpty = !neutral;
    }
    return undefined;
  }

  /**
   * 2.0.8 (adSchedule.playOnEmpty, product decision against C4/COST-5): the first pre-roll and every display tier
   * came back empty with the slot in view, so play clip 0 muted in the slot instead of the poster. The clip can float
   * as usual (C10: it played in the article) and the next clip's pre-roll is a second auction (preroll_1 budget).
   */
  async function playOnEmpty() {
    const decision = await coordinator.acquire(id, () => self.visible);
    if (!fallback) return undefined; // the reader tapped "Watch video" meanwhile
    if (decision !== 'video') return showWatch('▶\uFE0E Watch video');
    fallback.destroy();
    fallback = null;
    rebuildDom();
    return playClip(0, 'noad'); // video_start start_type=noad measures it
  }

  /** §3.2 step 1: video, chrome, listeners, float and token (GPT is handled by the fallback). */
  function teardownVideo() {
    cancel(self.heldAd);
    cancel(upNextTimer);
    self.pendingBreak = null;
    if (adController) {
      adController.destroy(); // adsManager.destroy(), adDisplayContainer.destroy()
      adController = null;
    }
    if (refs) {
      releaseVideoSource(refs.video); // pause(), removeAttribute('src'), load()
      unfloat('finished');
      refs.player.remove(); // video element, IMA container, poster, controls, header, captions, Up next
      refs.state.textContent = '';
      refs.state.appendChild(refs.statePoster);
    }
    for (const cleanup of cleanups.splice(0)) cleanup();
    if (coordinator.token.holder === id) coordinator.release(id, 'to_display');
  }

  // ---- float (§7) ----------------------------------------------------------

  /** The player scrolled fully out of view while playing. */
  function onScrolledOff() {
    if (!isAdPlaying() && !isContentPlaying()) return;
    // 2.0.2 (A42): clips float too (§7, C10), not only ads.
    if (!tryFloat()) {
      pauseInPlace();
      retryFloat();
    }
  }

  /**
   * 2.0.5 (A45): once the slot is fully out, the IntersectionObserver sends nothing more, so a float blocked
   * at that instant (sticky ad, banner, vignette) would never be tried again. Retry every 250 ms for at most
   * 10 s with every float check unchanged; stop once the slot is back in view, the tab is hidden or the player
   * went to the display fallback (which never floats). A closed player fails tryFloat() (FL-7); destroy() clears
   * the timer.
   * @param {number} [startedAt]
   */
  function retryFloat(startedAt = env.now()) {
    cancel(floatRetry);
    floatRetry = later(() => {
      const waited = env.now() - startedAt;
      // Over 10 s, a video appearing reads as an unexpected ad. Back in view, onIntersect() clears pausedBy.scroll.
      if (fallback || !self.fullyOut || !self.pausedBy.scroll || doc.visibilityState === 'hidden' || waited > 10000) return;
      if (!tryFloat(waited)) return retryFloat(startedAt);
      self.pausedBy.scroll = false;
      resumeAuto();
    }, 250);
  }

  /**
   * Float the playing player (ad or clip).
   * @param {number} [retryMs] set by retryFloat(): no qsvp_float_blocked per attempt, after_ms on success
   * @returns {boolean} floated
   */
  function tryFloat(retryMs) {
    const fSize = adFloatSize(); // C17: the float must be able to show an ad, whatever it shows now
    const decision = isFloatAllowed({
      floatEnabled: playerConfig.float !== false,
      closed: self.closed, // FL-7
      floatSize: fSize && isAdSizeAllowed(fSize) ? fSize : null, // C17 before floating
      isPhone: isPhone(),
      mobileFloat: config.mobileFloat,
      holdsToken: coordinator.canFloat(id),
      fullscreenAdOpen: safeArea.isFullscreenAdOpen(),
      inputFocused: !!doc.activeElement && /^(INPUT|TEXTAREA)$/.test(doc.activeElement.tagName), // §7.1 step 3
    });
    let { floated, reason } = decision.allowed ? floater.float() : decision;
    if (reason === 'density' && isPhone() && !self.smallFloat) {
      self.smallFloat = true; // kept for the page view: the sticky ad stays
      ({ floated, reason } = floater.float());
    }
    // 2.0.11 (stickyMakeRoom 'ad' | 'always' | 'off'): still over 30%, so remove sticky ads to make room. 'ad' only
    // while a video ad plays or is about to start: a clip with no ad earns less than the stickies.
    const room = config.stickyMakeRoom;
    // 2.0.16 (posterMakeRoom 'anchor' | 'off'): the poster may collapse the top anchor (never other stickies).
    const posterRoom = self.state === 'poster' && config.posterMakeRoom === 'anchor';
    if (reason === 'density' && (posterRoom || room === 'always' || (room === 'ad' && /^(ad|requesting)$/.test(self.state)))) {
      const [w, h] = floatNow();
      const removed = safeArea.makeRoom([w + 2, h + HEADER_HEIGHT + 2], [refs.player, refs.slot], posterRoom);
      if (removed.length) {
        track('qsvp_sticky_removed', { ad_units: removed.join(',') });
        ({ floated, reason } = floater.float());
      }
    }
    if (!decision.allowed) self.lastFloatDecision = { type: 'blocked', reason };
    if (floated) {
      coordinator.setFloatOwner(id);
      show('floating', { posterUrl: posterUrl(), dock: floater.state.dock });
      applyLayout(false); // A51: the ad takes the float size
      track('qsvp_float', { dock: floater.state.dock.replace('-', '_'), after_ms: retryMs });
      return true;
    }
    if (retryMs === undefined && /^(collision|density|token|small_screen)$/.test(reason)) track('qsvp_float_blocked', { reason });
    return false;
  }

  /** Static rules: pause in place, resume when back in view. */
  function pauseInPlace(reason = 'scrolled_off') {
    pauseEverything(reason);
    self.pausedBy.scroll = true;
  }

  /**
   * End the float.
   * @param {string} reason returned|closed|finished|collision
   */
  function unfloat(reason) {
    if (!floater || !floater.state.floating) return;
    floater.unfloat(reason);
    afterUnfloat(reason);
  }

  /**
   * Bookkeeping once the float has ended (by us or by a failed re-check).
   * @param {string} reason
   */
  function afterUnfloat(reason) {
    if (self.pausedBy.float) {
      // Paused while the float was hidden: resume only if the reader is back at the slot.
      self.pausedBy.float = false;
      if (reason === 'returned') resumeAuto();
    }
    coordinator.setFloatOwner(null);
    track('qsvp_float_end', { reason });
    if (refs && /^(ad|content|requesting|poster)$/.test(self.state)) show('player');
    applyLayout(false);
  }

  /** A recheck could not find a clear spot: un-float and pause (SA-7). */
  function onFloatLost() {
    const playing = self.pausedBy.float || isAdPlaying() || isContentPlaying();
    afterUnfloat('collision');
    pauseInPlace();
    // 2.0.5 (A45): float again once the obstacle has gone. 2.0.17: the poster too, so a top anchor that appears while
    // it floats is collapsed by the retry (posterMakeRoom) instead of leaving the poster in the off-screen slot.
    if (playing || self.state === 'poster') retryFloat();
  }

  // ---- pause / resume ------------------------------------------------------

  /**
   * Pause ad and content.
   * @param {string} reason qsvp_auto_pause reason
   * @returns {'ad'|'content'|null} what was playing
   */
  function pauseEverything(reason) {
    let was = null;
    if (isAdPlaying()) {
      adController.pause();
      setAdButton(false); // 2.0.5 (A49): an auto-paused ad shows ▶, not ❚❚
      was = 'ad';
    } else if (isContentPlaying()) {
      refs.video.pause();
      was = 'content';
    }
    if (was) track('qsvp_auto_pause', { reason });
    updatePlayButtons();
    return was;
  }

  /** Resume what was auto-paused (never what the user paused). */
  function resumeAuto() {
    if (self.pausedBy.yield || self.closed) return;
    if (self.state === 'ad' && adController) {
      adController.resume();
      setAdButton(true);
    } else if (self.state === 'content' && refs.video.paused && refs.video.getAttribute('src')) tryPlay();
    updatePlayButtons();
  }

  // ---- user actions (§7, §8) -----------------------------------------------

  /**
   * "▶ Watch video" / "▶ Watch next" / "▶ Watch again" (user-initiated only, FL-8).
   * @param {Event} event
   * @param {string} text
   */
  function onUserTap(event, text) {
    if (!env.isTrusted(event)) return; // FL-8: user-initiated only
    const wasClosed = self.closed;
    let index = self.clipIndex;
    if (text.includes('next')) {
      track('qsvp_up_next', { action: 'tap', next_clip_id: ctx.playlist[index] && ctx.playlist[index].id });
    } else if (text.includes('again')) {
      index = 0;
      counters.contentSeconds = 0; // assumption A20: a user replay is not held back by the automatic cap
      counters.clipsStarted = 0; // A40: nor by the loop's play count
    } else {
      track('qsvp_reopen', {});
    }
    if (wasClosed) self.closed = false;
    self.clickToPlay = true;
    self.pausedBy.yield = false;
    self.retriedClip = false;
    if (fallback) {
      // Only reachable from the all-empty state (no live ad): the user chose video (A2).
      fallback.destroy();
      fallback = null;
      rebuildDom();
    }
    coordinator.takeForUser(id); // §6: a user tap always takes the token
    if (adController) adController.cancel();
    requestBreak(index, 'click');
  }

  /** qsvp_dropoff: the viewer leaves, closes or pauses within 10 s of a pre-roll ending (§15.4). */
  function trackDropoff() {
    if (!self.adEndedAt || env.now() - self.adEndedAt > 10000) return;
    self.adEndedAt = 0;
    track('qsvp_dropoff', {
      ad_seconds: Math.round(budget.state.adSecondsServed),
      content_seconds: Math.round(budget.state.contentSecondsPlayed),
    });
  }

  /** Close (×): stop, destroy IMA, release source and token; poster + "▶ Watch video" (FL-6). */
  function close() {
    const stateAtClose = self.state === 'ad' ? 'ad' : self.state === 'content' ? 'content' : 'end_card';
    track('qsvp_close', { state_at_close: stateAtClose });
    trackDropoff();
    self.closed = true; // FL-7: never reopens, re-autoplays or re-floats by itself
    self.pendingBreak = null;
    cancel(self.heldAd);
    if (adController) {
      // C18: ad time already watched counts against the page budget.
      budget.addAdSeconds(Math.min(adController.adElapsed(), self.adDuration || 30));
      adController.cancel();
    }
    endPlayback('closed');
    setState('closed');
    showWatch('▶\uFE0E Watch video');
  }

  /**
   * Mute/unmute. Unmute only on a real user gesture (C8, UX-5).
   * @param {boolean} muted
   * @param {Event} [event]
   */
  function setMuted(muted, event) {
    if (!muted && (!event || !env.isTrusted(event))) return; // C8: sound only after a real user gesture
    self.muted = muted;
    if (refs) {
      refs.video.muted = muted;
      refs.mute.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      refs.player.toggleAttribute('data-muted', muted); // also picks the CSS speaker icon (A50)
      syncCaptions();
    }
    if (adController) adController.setMuted(muted);
    track(muted ? 'qsvp_mute' : 'qsvp_unmute', { during: self.state === 'ad' ? 'ad' : 'content' });
  }

  /**
   * Play/pause toggle from the control bar, header or keyboard.
   * @param {Event} [event]
   */
  function togglePlay(event) {
    if (self.state === 'poster') return onUserTap(event, '▶\uFE0E Watch video'); // 2.0.14: a tap on the poster
    if (self.state === 'ad' && adController) {
      const play = refs.adPlay.getAttribute('aria-label') !== 'Pause';
      if (play) {
        if (!holdsToken()) coordinator.takeForUser(id);
        self.pausedBy.yield = false;
        adController.resume();
      } else adController.pause();
      setAdButton(play);
      return;
    }
    if (self.state !== 'content') return;
    const video = refs.video;
    track(video.paused ? 'qsvp_play' : 'qsvp_pause', {
      clip_id: currentClip().id,
      video_current_time: Math.round(video.currentTime),
    });
    if (video.paused) {
      if (!holdsToken()) coordinator.takeForUser(id);
      self.pausedBy.yield = false;
      self.pausedBy.scroll = false;
      tryPlay();
    } else {
      trackDropoff();
      video.pause();
    }
    updatePlayButtons();
  }

  /**
   * Play/pause button state (clip or ad).
   * @param {Element} button
   * @param {boolean} playing
   */
  function setPlayButton(button, playing) {
    button.textContent = playing ? '❚❚' : '▶\uFE0E';
    button.setAttribute('aria-label', playing ? 'Pause' : 'Play');
  }

  /** @param {boolean} playing ad play/pause button (2.0.5, A49: also on auto pause/resume) */
  const setAdButton = (playing) => setPlayButton(refs.adPlay, playing);

  /** Keep play button labels in sync. */
  function updatePlayButtons() {
    if (refs) setPlayButton(refs.play, !refs.video.paused);
  }

  /** Fullscreen toggle; IMA resized via adsManager.resize (SZ-5). */
  function toggleFullscreen() {
    const target = refs.player;
    track('qsvp_fullscreen', { state: doc.fullscreenElement ? 'exit' : 'enter' });
    if (doc.fullscreenElement) {
      if (doc.exitFullscreen) doc.exitFullscreen();
    } else if (target.requestFullscreen) {
      target.requestFullscreen().catch(() => {});
    } else if (refs.video.webkitEnterFullscreen) {
      refs.video.webkitEnterFullscreen();
    }
  }

  /** Controls auto-hide after 3 s of no interaction during content (UX-7). */
  function wakeControls() {
    if (!refs) return;
    refs.player.removeAttribute('data-idle');
    cancel(idleTimer);
    idleTimer = later(() => {
      if (self.state === 'content' && !refs.video.paused) refs.player.setAttribute('data-idle', '');
    }, 3000);
  }

  // ---- coordinator callbacks -----------------------------------------------

  /**
   * The coordinator takes the token away (user tap on another player, or a partner player, C29).
   * @param {'user_tap'|'partner_player'} reason
   */
  function yieldToken(reason) {
    self.pausedBy.yield = true;
    if (reason === 'partner_player') self.clickToPlay = true; // stays click-to-play for the page view
    if (self.state === 'requesting') {
      self.pendingBreak = null;
      if (adController) adController.cancel();
      showWatch('▶\uFE0E Watch video');
      setState('idle', { reason });
      return;
    }
    pauseEverything(reason === 'partner_player' ? 'partner_player' : 'scrolled_off');
    unfloat('collision');
  }

  // ---- visibility and page events --------------------------------------------

  /**
   * IntersectionObserver entry for the slot.
   * @param {IntersectionObserverEntry} entry
   */
  function onIntersect(entry) {
    const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
    const wasVisible = self.visible;
    self.visibleRatio = ratio;
    self.visible = ratio >= LOCKED.visibilityThreshold; // C9
    self.fullyOut = ratio === 0;
    if (self.visible && !self.slotVisibleSent) {
      self.slotVisibleSent = true;
      track('qsvp_slot_visible', {});
    }
    if (self.visible) {
      self.early = false; // 2.0.12: seen now, so a held ad follows the normal rules
      const waiters = visibleWaiters;
      visibleWaiters = [];
      waiters.forEach((resolve) => resolve());
    }
    if (fallback) return; // fallbacks never float; their refresh timer uses GPT visibility events
    if (self.visible && floater && floater.state.floating) {
      unfloat('returned'); // FL-2
    }
    if (self.visible && !wasVisible) {
      if (self.state === 'idle' && !self.decided && !self.clickToPlay && !self.closed) decide();
      if (heldReady()) startAd();
      if (self.pausedBy.scroll) {
        self.pausedBy.scroll = false;
        resumeAuto();
      }
    }
    // 2.0.8 floatOnArrival: the reader is already past the slot, so decide now; the ad starts in the float (A54).
    if (pastSlot() && self.state === 'idle' && !self.decided && !self.clickToPlay && !self.closed) decide();
    if (self.fullyOut && (self.state === 'ad' || self.state === 'content') && !(floater && floater.state.floating)) {
      onScrolledOff();
    }
    // 2.0.7 (A54): an ad held while the slot was partly visible floats and starts once the slot is fully off screen.
    if (heldReady() && floatLate()) startAd();
    preloadHeld(); // 2.0.13
    // 2.0.14: the poster floats once the reader has scrolled past the slot.
    if (self.state === 'poster' && self.fullyOut && aboveViewport() && !(floater && floater.state.floating)) tryFloat();
  }

  /**
   * Tab hidden/visible (FL-9): pause everything; resume only what was playing.
   * @param {boolean} hidden
   */
  function onTabVisibility(hidden) {
    if (fallback) {
      fallback.onTabVisibility(!hidden);
      return;
    }
    if (hidden) {
      self.pausedBy.tab = pauseEverything('tab_hidden');
    } else if (self.pausedBy.tab || self.pausedBy.scroll) {
      // 2.0.3: also covers an ad paused as "offscreen_start" while the tab was hidden. With the slot already in
      // view there is no intersection change to resume it, so resume it here.
      self.pausedBy.tab = null;
      self.pausedBy.scroll = !canBeSeen();
      if (!self.pausedBy.scroll) resumeAuto();
    }
    if (!hidden && heldReady() && canBeSeen()) startAd();
  }

  /** Unexpected error: safe collapse respecting CLS, with a stack log (ERR-10). */
  function onUnexpectedError(error) {
    debug('error', { message: String(error && error.message), stack: String(error && error.stack) });
    track('qsvp_player_error', { error_code: 'script_error', stage: self.state });
    if (self.destroyed) return;
    try {
      teardownVideo();
      if (fallback) fallback.destroy();
    } catch (inner) {
      // Already broken: keep the reserved space.
    }
    if (isBelowViewport()) {
      setState('collapsed', { reason: 'script_error' });
      show('collapsed');
    } else {
      setState('collapsed', { reason: 'script_error_placeholder' });
      show('placeholder', {});
    }
  }

  // ---- DOM wiring -------------------------------------------------------------

  /** Build (or rebuild after a fallback) the player DOM and wire its controls. */
  function rebuildDom() {
    const heights = slotHeights(self.profile, self.size);
    refs = buildSlotDom({
      doc,
      slot,
      theme: config.theme,
      bodyHeight: heights.header + heights.frame,
      finePointer: env.finePointer(),
    });
    refs.player.setAttribute('data-muted', '');
    refs.poster.src = posterUrl();
    applyLayout(true);
    const video = refs.video;
    listen(video, 'timeupdate', onTimeUpdate);
    listen(video, 'seeking', () => tracker.markSeek(video.currentTime));
    listen(video, 'ended', onClipEnded);
    listen(video, 'error', () => {
      if (video.getAttribute('src')) onContentError();
    });
    listen(video, 'playing', () => {
      refs.poster.style.display = 'none';
      refs.player.removeAttribute('data-loading');
      updatePlayButtons();
      wakeControls();
    });
    listen(video, 'pause', updatePlayButtons);
    listen(video, 'waiting', () => refs.player.setAttribute('data-loading', ''));
    listen(refs.close, 'click', close);
    listen(refs.mute, 'click', (event) => setMuted(!self.muted, event));
    listen(refs.sound, 'click', (event) => setMuted(false, event));
    listen(refs.play, 'click', togglePlay);
    listen(refs.adPlay, 'click', togglePlay);
    listen(refs.full, 'click', toggleFullscreen);
    listen(refs.adFull, 'click', toggleFullscreen);
    listen(refs.upNextCancel, 'click', cancelUpNext);
    /** User seek: never counts as played time (§4); one qsvp_seek per 2 s at most. */
    const seekTo = (target) => {
      const from = video.currentTime;
      video.currentTime = Math.max(0, target);
      tracker.markSeek(video.currentTime);
      track('qsvp_seek', { from_s: Math.round(from), to_s: Math.round(video.currentTime) });
    };
    listen(refs.rewind, 'click', () => seekTo(video.currentTime - 10));
    listen(refs.seek, 'change', () => seekTo((Number(refs.seek.value) / 1000) * (video.duration || currentClip().duration)));
    listen(refs.volume, 'input', () => {
      video.volume = Number(refs.volume.value);
    });
    listen(refs.captions, 'click', () => {
      self.captionsUserSet = true;
      const on = refs.captions.getAttribute('aria-pressed') !== 'true';
      refs.captions.setAttribute('aria-pressed', on ? 'true' : 'false');
      syncCaptions();
      track('qsvp_captions', { state: on ? 'on' : 'off' });
    });
    listen(refs.stage, 'click', (event) => {
      if (event.target === video || event.target === refs.poster) togglePlay(event);
    });
    listen(refs.player, 'pointermove', wakeControls);
    listen(refs.player, 'keydown', (event) => {
      // UX-10: Space/K toggle play, M toggles mute.
      if (event.target !== refs.player && event.target !== video) return;
      if (event.key === ' ' || event.key === 'k' || event.key === 'K') {
        event.preventDefault();
        togglePlay(event);
      } else if (event.key === 'm' || event.key === 'M') {
        setMuted(!self.muted, event);
      }
    });
    listen(doc, 'fullscreenchange', () => {
      if (!adController) return;
      if (doc.fullscreenElement === refs.player) adController.resize(win.screen.width, win.screen.height, true);
      else if (currentAdSize()) adController.resize(currentAdSize()[0], currentAdSize()[1], false);
    });
    floater = createFloater({
      win,
      doc,
      refs,
      safeArea,
      getProfile: () => self.profile,
      getFloatSize: () => floatNow(),
      dockOrder: config.floatDock,
      article,
      requestFrame: env.requestFrame,
      onDecision: (decision) => {
        if (decision.type === 'suspend') {
          self.pausedBy.float = !!pauseEverything(decision.reason); // §7.1 / A33: pause while hidden
        } else if (decision.type === 'resume' && self.pausedBy.float) {
          self.pausedBy.float = false;
          resumeAuto();
        }
        // SA-13: the debug add-on outlines the obstacles and the dock spot.
        if (decision.obstacles) ctx.outline(decision.obstacles, decision.rect || null);
        delete decision.obstacles;
        self.lastFloatDecision = decision;
        debug('float', decision);
      },
    });
  }

  // ---- public API ---------------------------------------------------------------

  return {
    id,
    self,
    counters,
    get refs() {
      return refs;
    },
    get floater() {
      return floater;
    },
    get fallback() {
      return fallback;
    },
    get playlist() {
      return ctx.playlist;
    },
    /** Build the DOM and start observing (called once the gate has passed). */
    start() {
      applyLayout(true);
      rebuildDom();
      show('placeholder', { posterUrl: posterUrl() });
      coordinator.register(id, { yieldToken: safeCall(yieldToken) });
      observer = new env.IntersectionObserver(
        (entries) => entries.forEach(safeCall(onIntersect)),
        { threshold: [0, LOCKED.visibilityThreshold, 1] }, // C9: threshold never below 0.5 for autoplay
      );
      observer.observe(slot);
      track('qsvp_player_ready', {});
      // 2.0.8: fetch the IMA SDK at build (within lazyMarginPx, after consent, PRV-1), not at 50% visible, so the
      // ad request goes out sooner. Errors are left to requestBreak (ERR-1).
      // 2.0.12 (video.preRequest): request the pre-roll now; it still starts only at 50% visible (C9).
      if (config.video.preRequest) decide(true);
      else if (ctx.playlist.length && ctx.videoAllowed) {
        ctx.consentPromise.then(imaSdk).catch(() => {});
      }
    },
    /** Page scroll/resize/mutation tick while floating (SA-7). */
    onPageChange() {
      if (floater && floater.state.floating) floater.scheduleRecheck(safeCall(onFloatLost));
    },
    /** Viewport resized or rotated (debounced 150 ms by the app, SZ-6). */
    onViewportChange: safeCall(() => applyLayout(false)),
    onTabVisibility: safeCall(onTabVisibility),
    /** A text input got focus: pause and un-float (§7.1 step 3). */
    onInputFocus: safeCall(() => {
      if (floater && floater.state.floating) {
        unfloat('collision');
        pauseInPlace('input_focus');
      }
    }),
    /** pagehide: stop downloads (COST-4). */
    onPageHide: safeCall(() => {
      trackDropoff();
      if (refs) releaseVideoSource(refs.video);
    }),
    close: safeCall(close),
    /** SPA navigation / teardown (§9.1). */
    destroy() {
      if (self.destroyed) return;
      teardownVideo();
      self.destroyed = true;
      if (fallback) fallback.destroy();
      if (observer) observer.disconnect();
      timers.forEach((handle) => env.clearTimeout(handle));
      timers.clear();
      coordinator.unregister(id);
    },
  };
}
